import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Header,
  DataGrid,
  mergeRows,
  DataGridColumn,
  ProgressBar,
  SecurePay,
  PaymentError,
  TokenisedCard,
  DataGridSection,
  Alert,
} from "acma-spa";
import { InvoiceDetails } from "../components/invoiceDetails";
import NumberFormat from "react-number-format";
import { getApiClient, Invoice, IPaymentResponse } from "../api";
import { NavPage } from "../models/NavPage";
import { autoFocus } from "acma-spa";

export interface FormProps {
  baseUrl: string;
  clientId: string;
  merchantCode: string;
  securePaySrc: string;
  setPageState: (page: NavPage) => void;
  setReceiptState: (resp: IPaymentResponse) => void;
}

export const InvoiceForm: React.FC<FormProps> = (props) => {
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string>();
  const [error, setError] = useState<string>();
  const [rows, setRows] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [rowsDOM, setRowsDOM] = useState<Invoice[] | null>();
  const rowsDOMRef = useRef(rowsDOM);
  const securePayRef = useRef(null);
  const columns: DataGridColumn<Invoice>[] = [
    {
      label: "Invoice",
      value: (r) => r.invoiceNumber,
      footer: "Total",
    },
    {
      label: "Amount",
      rightAlign: true,
      value: (r) => (
        <NumberFormat
          value={r.outstandingAmount}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          prefix={"$"}
        />
      ),
      footer: (
        <NumberFormat
          value={rows.reduce((a, b) => a + b.outstandingAmount, 0)}
          displayType={"text"}
          thousandSeparator={true}
          decimalScale={2}
          fixedDecimalScale={true}
          prefix={"$"}
        />
      ),
    },
    {
      label: "",
      rightAlign: true,
      value: (r) =>
        r.invoiceNumber !== "Total" ? (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (confirm("Are you sure you want to remove this invoice?")) {
                const newRows = mergeRows(rows, r, null);
                setRows(newRows);
              }
            }}
          >
            Remove
          </a>
        ) : (
          <></>
        ),
    },
  ];

  const getInvoice = async (invoiceNumber: string) => {
    if (invoiceNumber === "") return;

    try {
      setError("");
      setLoading(true);

      const [client, abort] = getApiClient(props.baseUrl);
      const fetchedInvoice = await client.invoices(invoiceNumber);

      if (!fetchedInvoice) {
        setError("Invoice not found");
        autoFocus("#container");
        return null;
      }

      if (
        rows.some((item) => item.invoiceNumber == fetchedInvoice.invoiceNumber)
      ) {
        setError("Invoice added");
        autoFocus("#container");
        return null;
      }

      return fetchedInvoice;
    } catch (e) {
      alert("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    securePayRef.current.reset();
    setRows([]);
    setError("");
    setErrorMessages([]);
    setPaymentErrorMessage("");
  };

  const handlePay = () => {
    if (
      !confirm(
        "Confirm that this is the correct invoice number before you pay."
      )
    )
      return;

    setLoading(true);
    setRowsDOM(rows);
    securePayRef.current.tokenise();
  };

  const handlePaymentError = (errors: PaymentError[]) => {
    let errorMessages: any[] = [];
    errors.map((error, i) => {
      errorMessages.push(
        <p key={i}>
          {error.source && error.source !== undefined && error.source.pointer}{" "}
          {error.detail}
        </p>
      );
    });
    setErrorMessages(errorMessages);
    setLoading(false);
  };

  const handlePaymentSuccess = (tokenisedCard: TokenisedCard) => {
    makePayment(tokenisedCard.token);
  };

  const makePayment = async (token: string) => {
    let rows = rowsDOMRef.current;
    if (typeof rows === "undefined" || rows === null || rows.length == 0) {
      return;
    }

    try {
      const [client, abort] = getApiClient(props.baseUrl);
      let response = await client.pay(
        token,
        rows.map((i) => i.invoiceNumber ?? "")
      );
      switch (response.status) {
        case "limit":
          setPaymentErrorMessage(
            `Transaction unsuccessful, payment limit has been exceeded. (${response.gatewayResponseMessage})`
          );
          break;
        case "paid":
          props.setReceiptState(response);
          props.setPageState(NavPage.Confirmation);
          break;
        case "failed":
          setPaymentErrorMessage(
            `Transaction unsuccessful, please check your details and try again. (${response.gatewayResponseCode}: ${response.errorCode})`
          );
          break;
        default:
          break;
      }
    } catch (e) {
      alert("Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    rowsDOMRef.current = rowsDOM;
  }, [rowsDOM]);

  return (
    <>
      <ProgressBar total={2} current={1} />
      <Header label="Let’s get started!" />
      <DataGridSection>
        <InvoiceDetails
          r={null}
          onChange={async (update) => {
            let updated = await getInvoice(update?.invoiceNumber ?? "");
            if (
              typeof updated?.invoiceNumber === "undefined" ||
              updated?.invoiceNumber === ""
            )
              return;
            const newRows = mergeRows(rows, null, {
              invoiceNumber: updated.invoiceNumber,
              paid: updated.paid,
              outstandingAmount: updated.outstandingAmount,
            } as Invoice);
            setRows(newRows);
          }}
          errors={error || ""}
          disabled={loading}
        />
      </DataGridSection>
      <DataGrid<Invoice>
        columns={columns}
        rows={rows}
        noRowsMessage="Please add your invoices"
      ></DataGrid>
      <div className="d-flex">
        <div className="row">
          <div className="col mb-3">
            <b>Enter your payment details</b> (required)
          </div>
        </div>
      </div>
      <Alert messages={errorMessages} />
      {paymentErrorMessage && paymentErrorMessage != "" && (
        <Alert messages={[paymentErrorMessage]} />
      )}
      <SecurePay
        clientId={props.clientId ?? ""}
        merchantCode={props.merchantCode ?? ""}
        uri={`${props.securePaySrc}`}
        onLoadComplete={() =>
          setTimeout(() => {
            setLoading(false);
          }, 20)
        }
        onTokeniseError={handlePaymentError}
        onTokeniseSuccess={handlePaymentSuccess}
        ref={securePayRef}
      />
      <div className="row">
        <div className="col mb-3">
          <Button
            disabled={loading}
            mode="secondary"
            label="Reset"
            onClick={handleReset}
            className="mr-2"
          />
          <Button
            disabled={loading}
            label="Pay"
            onClick={handlePay}
            className="mr-2"
          />
        </div>
      </div>
    </>
  );
};
